import React, { useState } from 'react';
import { ChevronDown, Loader2, AlertCircle, Inbox, Construction } from 'lucide-react';
import { cn } from '../../utils/helpers';

// ============================================================================
// Section Card Wrapper
// ============================================================================

interface SectionCardProps {
  title: string;
  icon: React.ElementType;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
  actions?: React.ReactNode;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  title,
  icon: Icon,
  subtitle,
  children,
  className,
  collapsible = false,
  defaultOpen = true,
  actions,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={cn('card overflow-hidden', className)}>
      <button
        onClick={collapsible ? () => setOpen(!open) : undefined}
        className={cn(
          'w-full flex items-center justify-between px-5 py-4 border-b border-border/50',
          collapsible && 'cursor-pointer hover:bg-muted/20 transition-colors'
        )}
      >
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-primary/10">
            <Icon className="h-4 w-4 text-primary" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            {subtitle && (
              <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {actions}
          {collapsible && (
            <ChevronDown
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform',
                !open && '-rotate-90'
              )}
            />
          )}
        </div>
      </button>
      {open && <div className="p-5">{children}</div>}
    </div>
  );
};

// ============================================================================
// Info Row
// ============================================================================

interface InfoRowProps {
  label: string;
  value: React.ReactNode;
  icon?: React.ElementType;
  mono?: boolean;
}

export const InfoRow: React.FC<InfoRowProps> = ({ label, value, icon: Icon, mono }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-border/30 last:border-0">
    {Icon && (
      <div className="mt-0.5">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
    )}
    <div className="flex-1 min-w-0">
      <p className="text-[11px] text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className={cn('text-sm text-foreground mt-0.5', mono && 'font-mono text-xs')}>
        {value || <span className="text-muted-foreground/50">N/A</span>}
      </p>
    </div>
  </div>
);

// ============================================================================
// Metric Card
// ============================================================================

interface MetricCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  subtitle?: string;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  icon: Icon,
  color,
  bgColor,
  subtitle,
}) => (
  <div className="card p-4">
    <div className="flex items-center gap-3">
      <div className={cn('p-2 rounded-xl flex-shrink-0', bgColor)}>
        <Icon className={cn('h-4 w-4', color)} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] text-muted-foreground">{label}</p>
        <p className="text-lg font-bold text-foreground">{value}</p>
        {subtitle && (
          <p className="text-[10px] text-muted-foreground">{subtitle}</p>
        )}
      </div>
    </div>
  </div>
);

// ============================================================================
// Tab Loading State
// ============================================================================

export const TabLoading: React.FC = () => (
  <div className="flex items-center justify-center py-20">
    <Loader2 className="h-6 w-6 animate-spin text-primary" />
    <span className="ml-3 text-sm text-muted-foreground">Loading...</span>
  </div>
);

// ============================================================================
// Tab Error State
// ============================================================================

const NOT_AVAILABLE_MSG = 'not yet available';

export const TabError: React.FC<{ message: string; onRetry?: () => void; is404?: boolean }> = ({ message, onRetry, is404 }) => {
  const isNotAvailable = is404 || message.toLowerCase().includes(NOT_AVAILABLE_MSG) || message.includes('status code 404');

  return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      {isNotAvailable ? (
        <>
          <Construction className="h-8 w-8 text-amber-400/60" />
          <p className="text-sm font-medium text-foreground">Endpoint Not Available</p>
          <p className="text-xs text-muted-foreground max-w-sm text-center">
            This data requires a backend endpoint that hasn't been deployed yet. It will appear here once the API is live.
          </p>
        </>
      ) : (
        <>
          <AlertCircle className="h-8 w-8 text-red-400" />
          <p className="text-sm text-muted-foreground">{message}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="text-xs text-primary hover:text-primary/80 underline"
            >
              Retry
            </button>
          )}
        </>
      )}
    </div>
  );
};

// ============================================================================
// Tab Empty State
// ============================================================================

export const TabEmpty: React.FC<{ message?: string; icon?: React.ElementType }> = ({
  message = 'No data available',
  icon: Icon = Inbox,
}) => (
  <div className="flex flex-col items-center justify-center py-20 gap-3">
    <Icon className="h-8 w-8 text-muted-foreground/40" />
    <p className="text-sm text-muted-foreground">{message}</p>
  </div>
);

// ============================================================================
// User Mini Card (for displaying linked users)
// ============================================================================

interface UserMiniCardProps {
  user: {
    id: number;
    name: string | null;
    email?: string | null;
    profileImage?: string | null;
  };
  subtitle?: string;
  badge?: React.ReactNode;
  onClick?: () => void;
}

export const UserMiniCard: React.FC<UserMiniCardProps> = ({ user, subtitle, badge, onClick }) => (
  <div
    onClick={onClick}
    className={cn(
      'flex items-center gap-3 p-3 rounded-lg border border-border/50 bg-muted/5',
      onClick && 'cursor-pointer hover:bg-muted/20 transition-colors'
    )}
  >
    <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
      {user.profileImage ? (
        <img src={user.profileImage} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className="text-xs font-semibold text-primary">
          {(user.name || '?').charAt(0).toUpperCase()}
        </span>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-foreground truncate">{user.name || 'Unknown'}</p>
      {subtitle && <p className="text-[11px] text-muted-foreground">{subtitle}</p>}
      {user.email && !subtitle && <p className="text-[11px] text-muted-foreground truncate">{user.email}</p>}
    </div>
    {badge}
    <span className="text-[10px] text-muted-foreground font-mono">#{user.id}</span>
  </div>
);

// ============================================================================
// Status Badge
// ============================================================================

interface StatusBadgeProps {
  status: string;
  variant?: 'success' | 'warning' | 'error' | 'info' | 'default';
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, variant = 'default' }) => {
  const colors = {
    success: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    warning: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    error: 'bg-red-500/10 text-red-400 border-red-500/20',
    info: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    default: 'bg-muted text-muted-foreground border-border/50',
  };

  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border', colors[variant])}>
      {status}
    </span>
  );
};

export function getGenericStatusVariant(status: string): 'success' | 'warning' | 'error' | 'info' | 'default' {
  const s = status.toUpperCase();
  if (['ACTIVE', 'CLEAR', 'APPROVED', 'VERIFIED', 'COMPLETED', 'QUALIFIED', 'CREDITED', 'PAID', 'RESOLVED'].includes(s)) return 'success';
  if (['PENDING', 'PENDING_REVIEW', 'PROCESSING', 'SUBMITTED', 'IN_TRANSIT', 'PENDING_BALANCE'].includes(s)) return 'warning';
  if (['FAILED', 'REJECTED', 'CANCELLED', 'REVOKED', 'INVALIDATED', 'SUSPENDED', 'TERMINATED', 'DISMISSED', 'EXPIRED'].includes(s)) return 'error';
  if (['UNDER_REVIEW', 'IN_PROGRESS', 'CONSIDER'].includes(s)) return 'info';
  return 'default';
}
